<template>
  <Form
    :form-id="formId"
    :initial-values="initialValues"
    action="#"
    class="vp-row-form"
    :class="{ dirty }"
  >
    <HiddenInput input-id="id" />
    <VPInput
      ref="nameRef"
      :disabled="disabled"
      input-id="name"
      placeholder="Add list"
    />
    <div
      class="double-f relative flex"
      :class="{ 'has-value': hasType }"
    >
      <VPDropdown
        input-id="listType"
        placeholder="Choose list type"
        :options="listTypeOptions"
        @change="onTypeChange"
      />
    </div>
    <VPInput
      ref="projectedSoldRef"
      type="integer"
      :disabled="disabled"
      input-id="projected"
      placeholder="Add number"
      class="text-right"
    />
    <VPPriceInput
      ref="priceRef"
      :disabled="disabled"
      input-id="gross"
      placeholder="Add price"
      class="overflow-x-auto"
      hide-empty-input
    />
  </Form>
</template>

<script setup lang="ts">
import { Form, HiddenInput, useFormsStore } from 'vuniform';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import VPPriceInput from '@/components/form/VPPriceInput.vue';
import VPInput from '@/components/form/VPInput.vue';
import { ListTypeEnum } from '@/generated/graphql';
import VPDropdown from '@/components/form/VPDropdown.vue';

const formsStore = useFormsStore();

const nameRef = ref<VPInput>(null);
const typeRef = ref<VPDropdown>(null);
const projectedSoldRef = ref<VPInput>(null);
const priceRef = ref<VPPriceInput>(null);

interface Props {
  formId: string;
  initialValues: unknown;
  disabled?: boolean;
}

const props = defineProps<Props>();

const listTypeOptions = [
  {
    name: 'Guest list',
    value: ListTypeEnum.GuestList,
  },
  {
    name: 'Paid list',
    value: ListTypeEnum.PaidList,
  },
];

const hasType = computed(() => !!formsStore.fieldGetValue(props.formId, 'listType'));

const emit = defineEmits<{
  (e: 'blur'): void;
  (e: 'save'): void;
  (e: 'clear'): void;
}>();

const refs = [nameRef, typeRef, projectedSoldRef, priceRef];

const activeElement = ref<EventTarget | null>(null);

const focusChanged = (e: Event) => {
  activeElement.value = e.target;
};

onMounted(() => {
  document.addEventListener('focusin', focusChanged);
});
onBeforeUnmount(() => {
  document.removeEventListener('focusin', focusChanged);
});

const hasFocus = computed(() =>
  refs.some(r => r.value?.inputRef && activeElement.value && r.value?.inputRef === activeElement.value),
);

const hasValue = computed(() => {
  return ['name', 'projected', 'gross'].some(f => formsStore.fieldGetValue(props.formId, f));
});

watch(hasFocus, (newValue, oldValue) => {
  if (oldValue && !newValue) {
    if (hasValue.value) {
      emit('blur');
    } else {
      emit('clear');
    }
  }
});

const dirty = computed(() => formsStore.isFormDirty(props.formId));

const onTypeChange = () => {
  emit('save');
};
</script>
